import { ReactComponent as IconTick } from '@/assets/svgs/tick.svg'
import Button from '@/components/Button'
import useConfig from '@/hooks/use-config'
import useMessageGroups from '@/hooks/use-message-groups/index'
import API from '@/services/api/download.js'
import "@/translations/i18n"
import handle from '@/utils/handle.js'
import rem from '@/utils/rem'
import moment from 'moment'
import prettyBytes from 'pretty-bytes'
import React, { useEffect, useRef, useState } from 'react'
import DatePicker from "react-datepicker"
import { useTranslation } from "react-i18next"

const DownloadPage = ({ content, messageData }) => {

	const { t } = useTranslation();

	const [message, setMessage] = useState(messageData);

	const [image, setImage] = useState();
	const [csvPath, setCsvPath] = useState(null)
	const [jsonPath, setJsonPath] = useState(null)
	const [attachments, setAttachments] = useState([])
	const [size, setSize] = useState(0)
	const dateFrom = useRef(null)
	const dateTo = useRef(null)
	const [submitLoading, setSubmitLoading] = useState(true)
	const [errorMessage, setErrorMessage] = useState()
	const [allowDownload, setAllowDownload] = useState(false)

	const { exportMessage } = useMessageGroups()

	/*=================================================
		API
	/================================================*/

	const { config, fetch: fetchConfig, systems } = useConfig()

	useEffect(() => {
		fetchConfig()
		getData();
	}, [])

	const [startDate, setStartDate] = useState(Date.parse(messageData.dateFrom));
	const [endDate, setEndDate] = useState(Date.parse(messageData.dateTo));


	async function getData() {
		setAllowDownload(true)

		if(!startDate && !endDate){
			return setErrorMessage("Please provide data range to view.")
		}

		setErrorMessage()

		const payload = {
			dataElementId: message.dataElement.id,
			orgId: message.system.id,
			messageType: 'PULL',
			messageStatus: 'SUCCESS',
			dateFrom: moment(startDate).format('YYYY-MM-DD'),
			dateTo: moment(endDate).format('YYYY-MM-DD')
		}

		const [error, result] = await handle(exportMessage(payload))

		if (error) {
			return
		}

		setSubmitLoading(false)
		const mappedResult = { ...message, ...result,
								dataElement: {
									name: result.elementName,
									id: result.dataElementId
								}
							}

		const size = (!mappedResult.json || mappedResult.json.length === 0) ? 0 : JSON.stringify(mappedResult.json).length
		setAllowDownload((size > 0) ? false : true)
		setMessage(mappedResult)
	}

	useEffect(() => {

		if (!systems || !messageData) {
			return
		}

		const foundSystem = systems.find(system => system.id === messageData.system.id)

		if (!foundSystem?.name) {
			return
		}
		setImage(foundSystem.iconUrl)

	}, [systems])

	useEffect(() => {

		if (!message.attachments || message.attachments.length === 0) {
			setAttachments([])
		}

		setAttachments(message.attachments.map(attachment => {
			return { label: attachment.filename, value: attachment.file_content }
		}))

		const size = (!message.json || message.json.length === 0) ? 0 : JSON.stringify(message.json).length
		setSize(size)
		const jsonPath = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(message.jsonWithId))}`
		const csvPath = `data:text/csv;charset=utf-8,%EF%BB%BF${encodeURIComponent(message.csvWithId)}`
		setJsonPath(jsonPath)
		setCsvPath(csvPath)
	}, [message])

	const onDownload = async attachment => {
		try {
			const [error, result] = await API.download(attachment.value)
			const data = `data:application/pdf;base64,${result}`
			const link = document.createElement("a")
			link.href = data
			link.download = attachment.label
			link.click()
		} catch (err) {
			setErrorMessage(err)
		}
	}

	function onDownloadData(type) {
		try {
			const link = document.createElement("a");
			link.href = type === 'csv' ? csvPath : jsonPath;
			link.download = `${message.dataElement.name}.${type === 'csv' ? 'csv' : 'json'}`;
			document.body.appendChild(link);
			link.click();
		} catch (err) {
			setErrorMessage(err)
		}
	}

	const onDateChange = (dates) => {
		const [start, end] = dates;
		setAllowDownload(true)
		setStartDate(start);
		setEndDate(end);
	};

	return (
		<>
			<div className="download__component">
				<div className="download__component__header">
					<div className="download__component__header__left">
						<DatePicker
							// selected={startDate}
							onChange={onDateChange}
							startDate={startDate}
							endDate={endDate}
							selectsRange={true}
							isClearable
							placeholderText="Select Date From - Date To"
							minDate= {Date.parse(messageData.dateFrom)}
							maxDate= {Date.parse(messageData.dateTo)}
						/>
						{errorMessage && (<span className='input-field_required'>{errorMessage}</span>)}
					</div>
					<div className="download__component__header__right">
						<Button sizing='Medium' text={t("submit")} full slim maxWidth="100" submit={() => getData()} />
					</div>
				</div>

				{!message.json && !message.dataElement && (<div>{t("noResult")}</div>)}

				{(message.json || message.dataElement) && (
					<>
						<div className="download__component__content">

							<div className="download__component__content__left">
								<div className="download__component__content__cols">
									<div className="download__component__content__col">
										<div className="download__component__box">
											<div className="download__component__box__avatar">
												<img src={image}></img>
											</div>
											<div className="download__component__box__label">
												<div className="download__component__box__label__title">{message.dataElement.name}</div>
												<div className="download__component__box__label__name">{message.dataElement.id}</div>
												<div className="download__component__box__label__size">{prettyBytes(size)}</div>
											</div>
										</div>

										<div className="download__component__note">
											{content}
										</div>
									</div>
								</div>
							</div>
							<div className="download__component__content__right">
								<div className="download__component__content__cols">
									<div className="download__component__content__col download__component__content__col--greybg">
										<div className="download__component__box--column">
											<div className="download__component__right__header">
												{t("attachment")}
											</div>
											<div className="download__component__right__body">
												{attachments.map(attachment => (
													<div className="download__component__right__attachment" onClick={() => onDownload(attachment)}>
														<IconTick /> <span title={attachment.label}>{attachment.label}</span>
													</div>))
												}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="download__component__footer" style={{ marginTop: rem(35) }}>
							<div className="data-elements-actions__cols">
								<div className="data-elements-actions__col">
									<Button loading={submitLoading} sizing='Medium' text={t("downloadJSON")} full slim maxWidth='154' disabled={allowDownload} submit={() => onDownloadData('json')} />
								</div>
								<div className="data-elements-actions__col">
									<Button loading={submitLoading} sizing='Medium' text={t("downloadCSV")} full slim maxWidth='154' disabled={allowDownload} submit={() => onDownloadData('csv')} />
								</div>
							</div>
						</div>
					</>
				)}
			</div>
		</>
	)
}

export default DownloadPage;
